import React, { useState } from 'react';
import { Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import FaviconUploader from '../../../helpers/FaviconUploader';

// Each theme has exactly 4 colours: bg, text, accent, surface.
// The slug is what gets saved to the backend; colours are resolved from it.
const colorThemes = {
  'Neutral': [
    { slug: 'clean-white',   name: 'Clean White',    bg: '#ffffff', text: '#111111', accent: '#3366cc', surface: '#f5f5f5' },
    { slug: 'soft-gray',     name: 'Soft Gray',      bg: '#f5f5f5', text: '#222222', accent: '#4a6fa5', surface: '#e8e8e8' },
    { slug: 'warm-paper',    name: 'Warm Paper',     bg: '#faf7f2', text: '#2d2d2d', accent: '#8b5e3c', surface: '#f0ebe0' },
    { slug: 'slate',         name: 'Slate',           bg: '#f0f4f8', text: '#1a202c', accent: '#3182ce', surface: '#e2e8f0' },
  ],
  'Banking & Professional': [
    { slug: 'trust-blue',    name: 'Trust Blue',     bg: '#f0f5ff', text: '#1a3a6b', accent: '#1a3a6b', surface: '#dbeafe' },
    { slug: 'navy',          name: 'Navy',            bg: '#0d2b6e', text: '#ffffff', accent: '#60a5fa', surface: '#1e3a8a' },
    { slug: 'corporate',     name: 'Corporate',      bg: '#e8eefa', text: '#1a3a6b', accent: '#1d4ed8', surface: '#c7d7f8' },
    { slug: 'midnight',      name: 'Midnight',       bg: '#0a1628', text: '#e8f0fe', accent: '#60a5fa', surface: '#162035' },
  ],
  'Creative & Warm': [
    { slug: 'sunset',        name: 'Sunset',         bg: '#fff5ec', text: '#7d3c0a', accent: '#c2410c', surface: '#fed7aa' },
    { slug: 'rust',          name: 'Rust',            bg: '#2d1208', text: '#f5b088', accent: '#f97316', surface: '#431a0a' },
    { slug: 'terracotta',    name: 'Terracotta',     bg: '#f9ede8', text: '#8b3a2a', accent: '#c2410c', surface: '#f0d0c5' },
    { slug: 'amber',         name: 'Amber',           bg: '#fffbeb', text: '#78350f', accent: '#d97706', surface: '#fde68a' },
  ],
  'Nature & Green': [
    { slug: 'forest',        name: 'Forest',         bg: '#1a2f1a', text: '#c8e6c9', accent: '#4ade80', surface: '#2d4a2d' },
    { slug: 'sage',          name: 'Sage',            bg: '#f1f5f0', text: '#2d4a2d', accent: '#16a34a', surface: '#dcf0d8' },
    { slug: 'emerald',       name: 'Emerald',        bg: '#063323', text: '#d1fae5', accent: '#34d399', surface: '#065033' },
    { slug: 'olive',         name: 'Olive',           bg: '#f5f5e8', text: '#3d4a1e', accent: '#65a30d', surface: '#e5e8d0' },
  ],
  'Artists & High Contrast': [
    { slug: 'studio-black',  name: 'Studio Black',  bg: '#000000', text: '#ffffff', accent: '#ffffff', surface: '#1a1a1a' },
    { slug: 'gallery-white', name: 'Gallery White', bg: '#ffffff', text: '#000000', accent: '#000000', surface: '#f0f0f0' },
    { slug: 'noir',          name: 'Noir',            bg: '#0d0d0d', text: '#f0f0f0', accent: '#e0e0e0', surface: '#1a1a1a' },
    { slug: 'chalk',         name: 'Chalk',           bg: '#f2f2f2', text: '#0a0a0a', accent: '#0a0a0a', surface: '#e0e0e0' },
  ],
  'Dark & Elegant': [
    { slug: 'deep-purple',   name: 'Deep Purple',   bg: '#1a0a2e', text: '#e8d5ff', accent: '#a855f7', surface: '#2d1052' },
    { slug: 'dark-rose',     name: 'Dark Rose',     bg: '#1f0f14', text: '#ffd1dc', accent: '#f472b6', surface: '#3d1a25' },
    { slug: 'charcoal',      name: 'Charcoal',       bg: '#1c1c1e', text: '#f2f2f7', accent: '#636366', surface: '#2c2c2e' },
    { slug: 'deep-teal',     name: 'Deep Teal',     bg: '#0a1f1e', text: '#a7f3d0', accent: '#34d399', surface: '#0f3330' },
  ],
};

const allThemes = Object.values(colorThemes).flat();

function findTheme(slug) {
  return allThemes.find(t => t.slug === slug) || null;
}

function Visual({ website, onUpdated }) {
  const fontGroups = {
    'Sans Serif': [
      'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Nunito', 'Noto Sans', 'PT Sans', 'Rubik',
      'Work Sans', 'Ubuntu', 'Roboto Condensed', 'Quicksand', 'Fira Sans', 'Karla', 'Mulish', 'Heebo', 'DM Sans',
      'Titillium Web', 'Cabin', 'Manrope', 'Source Sans 3', 'IBM Plex Sans', 'Raleway'
    ],
    'Serif': [
      'Merriweather', 'Playfair Display', 'Lora', 'PT Serif', 'Roboto Slab', 'Bitter', 'Cardo', 'Source Serif 4', 'Noto Serif', 'Cinzel'
    ],
    'Display': [
      'Oswald', 'Bebas Neue', 'Abril Fatface', 'Anton', 'Bangers', 'Josefin Sans'
    ],
    'Handwriting': [
      'Pacifico', 'Dancing Script', 'Satisfy', 'Caveat', 'Indie Flower', 'Amatic SC', 'Lobster'
    ],
    'Monospace': [
      'Inconsolata', 'Source Code Pro', 'Fira Code', 'JetBrains Mono', 'Ubuntu Mono', 'Anonymous Pro'
    ]
  };

  const allFonts = Object.values(fontGroups).flat();

  const vs = (website && (website.visualSettings || website.visual_settings)) || {};
  const initialTheme = findTheme(vs.theme) || allThemes[0];

  const [faviconUrl, setFaviconUrl] = useState(
    website?.domain ? `https://${website.domain}/img/apple-touch-icon.png` : null
  );
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(initialTheme);
  const [form, setForm] = useState({
    textGlow: false,
    font: 'Quicksand',
    normalFontWeight: 400,
    boldFontWeight: 700,
    ...vs,
  });

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const isValidWeight = (val) => {
    const n = Number(val);
    return Number.isInteger(n) && n >= 100 && n <= 900 && n % 100 === 0;
  };

  const selectTheme = (theme) => setSelectedTheme(theme);

  const save = async (e) => {
    e.preventDefault();
    if (!allFonts.includes(form.font)) {
      toast.error('Font must be selected from the provided Google fonts list.');
      return;
    }
    if (!isValidWeight(form.normalFontWeight) || !isValidWeight(form.boldFontWeight)) {
      toast.error('Font weights must be 100–900 in steps of 100 (e.g., 400 for normal, 700 for bold).');
      return;
    }
    try {
      setSaving(true);
      const payload = { visualSettings: {
        theme: selectedTheme.slug,
        textGlow: !!form.textGlow,
        font: form.font,
        normalFontWeight: Number(form.normalFontWeight),
        boldFontWeight: Number(form.boldFontWeight),
      }};
      const resp = await patch(`websites/${website.id}/visual-settings`, payload);
      const updated = resp?.website || { ...website, visualSettings: payload.visualSettings };
      onUpdated?.(updated);
    } catch (e) {
      toast.error('Failed to save visual settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="favicon-section">
        <p className="favicon-label">Favicon</p>
        <FaviconUploader
          websiteId={website.id}
          currentFaviconUrl={faviconUrl}
          onUploaded={(data) => {
            const url = data?.url || data?.file?.url || data?.data?.url || null;
            if (url) setFaviconUrl(url);
          }}
        />
      </div>
      <hr className="mb-4" />

      <Form onSubmit={save}>
        <div className="theme-selector">
          {Object.entries(colorThemes).map(([group, themes]) => (
            <div key={group} className="theme-group">
              <div className="theme-group-label">{group}</div>
              <div className="theme-grid">
                {themes.map(theme => (
                  <button
                    key={theme.slug}
                    type="button"
                    className={`theme-card${selectedTheme?.slug === theme.slug ? ' theme-card--selected' : ''}`}
                    onClick={() => selectTheme(theme)}
                    style={{ backgroundColor: theme.bg }}
                    title={theme.name}
                  >
                    <span className="theme-card__preview" style={{ color: theme.text }}>Aa</span>
                    <div className="theme-card__swatches">
                      <span className="theme-card__swatch" style={{ background: theme.accent }}  title={`Accent: ${theme.accent}`} />
                      <span className="theme-card__swatch" style={{ background: theme.surface }} title={`Surface: ${theme.surface}`} />
                    </div>
                    <span className="theme-card__name" style={{ color: theme.text }}>{theme.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Form.Group className="mb-3 form-row-horizontal" controlId="vsGlow">
          <Form.Label>Text glow</Form.Label>
          <Form.Check
            type="checkbox"
            checked={!!form.textGlow}
            onChange={(e) => setForm({ ...form, textGlow: e.target.checked })}
            label={form.textGlow ? 'Enabled' : 'Disabled'}
          />
        </Form.Group>

        <Form.Group className="mb-3 form-row-horizontal" controlId="vsFont">
          <Form.Label>Font</Form.Label>
          <Form.Select
            value={form.font}
            onChange={(e) => setForm({ ...form, font: e.target.value })}
          >
            {Object.entries(fontGroups).map(([group, fonts]) => (
              <optgroup key={group} label={group}>
                {fonts.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </optgroup>
            ))}
          </Form.Select>
        </Form.Group>

        <Row>
          <Col>
            <Form.Group className="mb-3 form-row-horizontal" controlId="vsWeightNormal">
              <Form.Label>Font weight (normal)</Form.Label>
              <Form.Control
                type="number" min={100} max={900} step={100}
                value={form.normalFontWeight}
                onChange={(e) => setForm({ ...form, normalFontWeight: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3 form-row-horizontal" controlId="vsWeightBold">
              <Form.Label>Font weight (bold)</Form.Label>
              <Form.Control
                type="number" min={100} max={900} step={100}
                value={form.boldFontWeight}
                onChange={(e) => setForm({ ...form, boldFontWeight: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="form-actions">
          <Button type="submit" className="primary-button" disabled={saving}>
            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default Visual;
